import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';

// Per-type style overrides (ignores API colors)
const POLYGON_STYLES = {
  house:  { fill: 'rgba(230, 50, 50, 0.4)',  stroke: 'rgba(230, 50, 50, 0.9)',  outlineOnly: false },
  lawn:   { fill: 'rgba(50, 100, 230, 0.3)', stroke: 'none',                     outlineOnly: false },
  parcel: { fill: 'transparent',              stroke: '#DAA520',                  outlineOnly: true },
};

const DEFAULT_STYLE = { fill: 'rgba(255, 255, 0, 0.4)', stroke: 'rgba(255, 255, 0, 0.8)', outlineOnly: false };

/**
 * Extract rings from a polygon entry.
 * API format: { rings: [[[x,y], ...]], color: "#hex" }
 * Also handles plain arrays: [[[x,y], ...]] or [[x,y], ...]
 */
const extractRings = (value) => {
  if (!value) return [];

  let ringData;
  if (Array.isArray(value)) {
    ringData = value;
  } else if (typeof value === 'object') {
    ringData = value.rings || value.coordinates || value.polygon || value.points;
  }

  if (!Array.isArray(ringData) || ringData.length === 0) return [];

  const first = ringData[0];
  if (!Array.isArray(first)) return [];

  // Single ring [[x,y], [x,y], ...] vs array of rings [[[x,y], ...], ...]
  if (first.length === 2 && typeof first[0] === 'number') {
    return [ringData]; // wrap single ring
  }
  return ringData; // already array of rings
};

const PropertyImage = ({ imageData, imageSize, polygons, height = '100%' }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [ready, setReady] = useState(false);

  const drawPolygons = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !polygons) return;

    const imgRect = img.getBoundingClientRect();
    const containerWidth = imgRect.width;
    const containerHeight = imgRect.height;
    if (containerWidth === 0 || containerHeight === 0) return;

    const natW = imageSize ? imageSize.width : img.naturalWidth;
    const natH = imageSize ? imageSize.height : img.naturalHeight;
    if (!natW || !natH) return;

    // Calculate objectFit: contain rendering area
    const imgAspect = natW / natH;
    const containerAspect = containerWidth / containerHeight;
    let renderWidth, renderHeight, offsetX, offsetY;

    if (imgAspect > containerAspect) {
      renderWidth = containerWidth;
      renderHeight = containerWidth / imgAspect;
      offsetX = 0;
      offsetY = (containerHeight - renderHeight) / 2;
    } else {
      renderHeight = containerHeight;
      renderWidth = containerHeight * imgAspect;
      offsetX = (containerWidth - renderWidth) / 2;
      offsetY = 0;
    }

    // Position canvas exactly over the rendered image
    canvas.style.left = offsetX + 'px';
    canvas.style.top = offsetY + 'px';
    canvas.style.width = renderWidth + 'px';
    canvas.style.height = renderHeight + 'px';

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(renderWidth * dpr);
    canvas.height = Math.round(renderHeight * dpr);

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scaleX = (renderWidth * dpr) / natW;
    const scaleY = (renderHeight * dpr) / natH;

    // Draw order: parcel (outline) first, then lawn, then house on top
    const drawOrder = ['parcel', 'lawn', 'house'];
    const sortedEntries = Object.entries(polygons).sort(([a], [b]) => {
      const ai = drawOrder.indexOf(a);
      const bi = drawOrder.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    sortedEntries.forEach(([type, value]) => {
      const rings = extractRings(value);
      if (rings.length === 0) return;

      const style = POLYGON_STYLES[type] || DEFAULT_STYLE;

      rings.forEach((ring) => {
        if (!Array.isArray(ring) || ring.length < 3) return;
        ctx.beginPath();
        ctx.moveTo(ring[0][0] * scaleX, ring[0][1] * scaleY);
        for (let i = 1; i < ring.length; i++) {
          ctx.lineTo(ring[i][0] * scaleX, ring[i][1] * scaleY);
        }
        ctx.closePath();

        if (!style.outlineOnly) {
          ctx.fillStyle = style.fill;
          ctx.fill();
        }

        if (style.stroke && style.stroke !== 'none') {
          ctx.strokeStyle = style.stroke;
          ctx.lineWidth = style.outlineOnly ? 3 * dpr : 2 * dpr;
          ctx.stroke();
        }
      });
    });
  }, [imageSize, polygons]);

  const handleImageLoad = useCallback(() => {
    setReady(true);
    requestAnimationFrame(() => {
      drawPolygons();
    });
  }, [drawPolygons]);

  useEffect(() => {
    if (ready) {
      requestAnimationFrame(() => {
        drawPolygons();
      });
    }
  }, [ready, drawPolygons]);

  useEffect(() => {
    if (!ready) return;
    const handleResize = () => requestAnimationFrame(() => drawPolygons());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [ready, drawPolygons]);

  if (!imageData) return null;

  return (
    <Box style={{ position: 'relative', width: '100%', height: height, overflow: 'hidden' }}>
      <img
        ref={imgRef}
        src={`data:image/png;base64,${imageData}`}
        alt="Aerial property view"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        onLoad={handleImageLoad}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default PropertyImage;
